// Top-level error boundary (T-244).
//
// Before this existed, an uncaught React render error left the
// `<body>` empty and the user staring at a white page with no
// signal — the failure mode observed when polling responses got
// huge during a real-bundle import (the browser tab OOM'd, the
// renderer threw, and React unmounted the tree with nothing in
// the boundary to catch it).
//
// React error boundaries MUST be class components; the only
// place a class lives in this codebase.

import React from 'react';

interface State {
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

interface Props {
  children: React.ReactNode;
}

export class AppErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null, errorInfo: null };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('AppErrorBoundary caught', error, errorInfo);
    this.setState({ errorInfo });
  }

  handleReset = () => {
    this.setState({ error: null, errorInfo: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    const message = this.state.error.message || String(this.state.error);
    return (
      <div
        className="min-h-screen flex items-center justify-center bg-slate-50 px-6"
        data-testid="app-error-boundary"
        role="alert"
      >
        <div className="max-w-lg w-full bg-white rounded-3xl border border-rose-200 p-8 shadow-sm">
          <h1 className="text-xl font-bold text-rose-700 mb-2">
            Something went wrong.
          </h1>
          <p className="text-sm text-slate-700 mb-4">
            The page hit an unexpected error and stopped rendering.
            Your data is safe — this is a client-side display crash.
          </p>
          <pre
            className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-xs text-slate-700 overflow-auto max-h-48 whitespace-pre-wrap"
            data-testid="app-error-boundary-message"
          >
            {message}
          </pre>
          <div className="flex gap-3 mt-5">
            <button
              type="button"
              onClick={this.handleReload}
              className="bg-[#6344C6] hover:bg-[#5234b6] text-white font-bold px-4 py-2 rounded-xl text-sm shadow-sm transition-colors"
            >
              Reload the page
            </button>
            <button
              type="button"
              onClick={this.handleReset}
              className="bg-white border border-slate-200 hover:bg-slate-100 text-slate-700 font-semibold px-4 py-2 rounded-xl text-sm transition-colors"
            >
              Try to recover
            </button>
          </div>
          <p className="text-xs text-slate-500 mt-4">
            If this keeps happening after a reload, open the browser
            console (F12) and share the stack trace with support.
          </p>
        </div>
      </div>
    );
  }
}
